import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  Copy,
  Link as LinkIcon,
  RefreshCw,
  ScanBarcode,
} from "lucide-react";
import { ToolLayout } from "@/components/layout/ToolLayout";
import { TableScroll } from "@/components/layout/ContentBlock";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { FaqSection } from "@/components/seo/FaqSection";
import { toast } from "sonner";
import { ToolSeoSections } from "@/components/seo/ToolSeoSections";
import { pageHead, softwareAppJsonLd, breadcrumbJsonLd, ldScript, absoluteUrl } from "@/lib/seo";
import { parseGs1, type ParseResult } from "@/lib/gs1";
import { usePrefillValue } from "@/lib/usePrefillValue";

const PATH = "/gs1-digital-link-decoder";
const TITLE = "GS1 Digital Link Decoder — Parse GS1 URLs & Element Strings";
const DESC =
  "Free GS1 Digital Link decoder. Paste a GS1 QR URL, element string or raw payload to extract GTIN, batch, expiry, serial and other Application Identifiers.";

const FAQ = [
  {
    q: "What Is GS1 Digital Link?",
    a: "GS1 Digital Link is a standardized way to express GS1 Application Identifiers (GTIN, batch, expiry, serial, GLN…) inside a regular https:// URL. The same QR code becomes a web link for shoppers, a traceability key for retailers, and a data carrier for supply-chain systems — replacing single-purpose barcodes with one consumer-friendly URL.",
  },
  {
    q: "What's the Difference Between a GS1 Digital Link and a Plain QR Code?",
    a: "A plain QR code can encode anything. A GS1 Digital Link QR code encodes a URL whose path follows the GS1 syntax, e.g. https://id.gs1.org/01/07640156790000/10/ABC123. That structure lets any compliant scanner extract the GTIN and batch deterministically — no proprietary parsing required.",
  },
  {
    q: "Which Application Identifiers Does This Decoder Support?",
    a: "It recognises the most common AIs: (00) SSCC, (01) GTIN, (10) batch/lot, (11/13/15/17) production/packaging/best-before/expiry dates, (21) serial, (22) CPV, (240) additional product id, (30/37) counts, (414) GLN, (8003) GRAI, (8004) GIAI, (8006) ITIP, (8010) CPID, (8017/8018) GSRN. Unknown AIs are still listed but flagged as unrecognised.",
  },
  {
    q: "Is This an Official GS1 Verifier?",
    a: "No. ReadBarcode is independent from GS1. This tool performs structural parsing only — it does not verify that a GTIN is officially registered to a brand owner, nor does it confirm certification against the GS1 Digital Link Standard. For authoritative verification use the official GS1 Verified by GS1 service.",
  },
  {
    q: "Why Does Sunrise 2027 Matter for This Tool?",
    a: "From 2027, GS1 expects retail point-of-sale systems worldwide to accept 2D codes (QR / Data Matrix) carrying GS1 Digital Link in addition to traditional EAN/UPC. Brands are migrating now, which is why a Digital Link decoder is a daily-driver tool, not a niche one.",
  },
  {
    q: "Can I Paste a Payload Scanned From a Data Matrix?",
    a: "Yes. Paste it as an element string with parentheses, e.g. (01)07640156790000(17)260131(10)ABC123(21)42, or as a raw FNC1-separated payload using ^ or {GS} as the group separator. The decoder auto-detects the shape.",
  },
  {
    q: "Is There a GS1 Digital Link App?",
    a: "You do not need to install one. Any phone camera that reads QR codes opens a GS1 Digital Link as a web page, but it does not show the GTIN, lot or expiry as separate fields. To see them, copy the URL and paste it into this decoder, which runs in the browser on phones and desktops.",
  },
  {
    q: "Can I Decode a GS1 Digital Link From a Photo?",
    a: "Not on this page, which decodes text only. Read the code first with the QR Code Reader or the Data Matrix Reader, which both accept an uploaded photo, then paste the decoded text here. A sharp photo in which the code fills most of the frame reads most reliably.",
  },
];

const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "GS1 & Inventory", path: "/gs1-inventory" },
  { name: "GS1 Digital Link Decoder", path: PATH },
];

export const Route = createFileRoute("/gs1-digital-link-decoder")({
  component: Gs1DecoderPage,
  head: () => {
    const base = pageHead({
      title: TITLE,
      description: DESC,
      path: PATH,
    });
    return {
      ...base,
      scripts: [
        ldScript(
          softwareAppJsonLd({
            name: "ReadBarcode GS1 Digital Link Decoder",
            description: DESC,
            path: PATH,
          }),
        ),
        ldScript({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: "Understanding GS1 Digital Link",
          description: DESC,
          mainEntityOfPage: absoluteUrl(PATH),
          author: { "@type": "Organization", name: "ReadBarcode" },
          publisher: { "@type": "Organization", name: "ReadBarcode" },
        }),
        ldScript(breadcrumbJsonLd(CRUMBS)),
      ],
    };
  },
});

function Gs1DecoderPage() {
  return (
    <ToolLayout
      eyebrow="Online Tool"
      title="GS1 Digital Link Decoder"
      description="Paste a GS1 Digital Link URL, a (AI)value element string, or a raw FNC1-separated payload. We'll extract the GTIN, batch, expiry, serial and any other recognised Application Identifiers — all in your browser. Dates in YYMMDD form are shown as calendar dates, and every decoded value has its own copy button."
      crumbs={CRUMBS}
      content={
        <>
          <SupportedAis />
          <DigitalLinkExplainer />
          <ToolSeoSections
            aboutHeading="About This GS1 Digital Link Decoder"
            about={
              <>
                <p>
                  This GS1 Digital Link decoder parses GS1-formatted URLs and element strings into their
                  underlying Application Identifiers. Paste a scanned QR code value (often something
                  like <code>https://id.gs1.org/01/…</code>), a bracketed element string from a Data
                  Matrix, or a raw FNC1-separated payload — the decoder figures out which shape you gave
                  it and breaks it down.
                </p>
                <p>
                  It's the fastest way to inspect a GS1 QR code without spinning up a full supply-chain
                  stack. Everything is parsed locally; nothing is sent to a server.
                </p>
              </>
            }
            whenToUse={[
              "Inspecting a GS1 QR code on a product to confirm the GTIN, batch and expiry match the printed label.",
              "Debugging why a Digital Link URL isn't routing correctly in your e-commerce stack.",
              "Comparing a supplier's GS1 DataMatrix payload against the spec they sent you.",
              "Teaching colleagues how GS1 Application Identifiers map to Digital Link path segments.",
              "Preparing for Sunrise 2027 by validating that your brand's 2D codes parse cleanly.",
            ]}
            howItWorks={[
              {
                title: "Paste a payload",
                desc: "Drop in a Digital Link URL, an (AI)value element string, or a raw GS1 payload using ^ / {GS} as separators.",
              },
              {
                title: "Auto-detect the shape",
                desc: "URLs are parsed via the Digital Link path/query syntax; bracketed strings via element-string rules; raw strings via the AI tables.",
              },
              {
                title: "Resolve each AI",
                desc: "Each segment is matched against the known Application Identifier table — fixed-length AIs are split deterministically, variable ones end at the GS separator.",
              },
              {
                title: "Render result cards",
                desc: "Each AI becomes a card with its number, label, raw value and human-friendly formatting (e.g. 260131 → 2026-01-31).",
              },
              {
                title: "Copy what you need",
                desc: "Copy any individual value, or grab the whole normalised element string to pass to another tool.",
              },
            ]}
            whatToAvoid={[
              "Treating this as an official GS1 verifier — it parses structure, it does not certify compliance.",
              "Pasting an arbitrary URL and expecting Digital Link semantics. The path must follow /AI/value/AI/value/… or the GS1 shortName equivalents.",
              "Assuming every (01) value is a registered GTIN — only that the digits are present.",
              "Stripping the GS / FNC1 separators from a raw GS1 payload — variable-length AIs need them to know where to stop.",
              "Relying on the date formatter for partial dates (DD = 00 means 'last day of month' in GS1).",
            ]}
            tips={[
              "If a date looks wrong, remember GS1 uses YYMMDD with the GS1 century rule (YY ≤ 50 → 20YY, else 19YY).",
              "Variable-length AIs like (10) batch and (21) serial cap at 20 characters per the GS1 General Specifications.",
              "Need to validate the GTIN inside a Digital Link? Copy the (01) value into the GTIN Validator.",
              "Some scanners deliver GS1 payloads with the GS group separator replaced by ']' or '^' — both are handled here.",
              "Brands often expose multiple Digital Link redirects per GTIN (consumer page, technical product info). The decoder shows the structured data regardless of where the URL routes.",
            ]}
          />
          <SunriseCallout />
          <DecodeHowTo />
          <DigitalLinkVsElementString />
          <FaqSection items={FAQ} />
        </>
      }
      related={<RelatedTools />}
    >
      <DecoderApp />
    </ToolLayout>
  );
}

const EXAMPLES = [
  "https://id.gs1.org/01/07640156790000/10/ABC123?17=260131&21=42",
  "(01)07640156790000(17)260131(10)ABC123(21)42",
  "010764015679000017260131" + "10ABC123" + "\u001D" + "2142",
];

function DecoderApp() {
  const [value, setValue] = useState("");
  const [submitted, setSubmitted] = useState(false);
  usePrefillValue((v) => {
    setValue(v);
    setSubmitted(true);
  });

  const result: ParseResult | null = useMemo(() => {
    if (!submitted) return null;
    return parseGs1(value);
  }, [submitted, value]);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  const reset = () => {
    setValue("");
    setSubmitted(false);
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[1.1fr_1fr]">
      <Card>
        <CardContent className="p-5 md:p-6">
          <form onSubmit={onSubmit} className="flex flex-col gap-3">
            <label htmlFor="gs1-input" className="text-sm font-medium">
              GS1 Digital Link, element string, or raw payload
            </label>
            <Textarea
              id="gs1-input"
              rows={4}
              autoComplete="off"
              placeholder="https://id.gs1.org/01/07640156790000/10/ABC123?17=260131&21=42"
              value={value}
              onChange={(e) => {
                setValue(e.target.value);
                setSubmitted(false);
              }}
              className="font-mono text-sm"
              aria-invalid={result?.ok === false}
            />
            <div className="flex flex-wrap gap-2">
              <Button type="submit" disabled={!value.trim()}>
                <ScanBarcode className="mr-2 h-4 w-4" /> Decode
              </Button>
              <Button type="button" size="sm" variant="ghost" onClick={reset}>
                <RefreshCw className="mr-2 h-3.5 w-3.5" /> Clear
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              The decoder runs locally. Nothing is uploaded.
            </p>
            <div className="flex flex-wrap gap-2 pt-1 text-xs">
              {EXAMPLES.map((ex, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => {
                    setValue(ex);
                    setSubmitted(true);
                  }}
                  className="rounded-md border border-border bg-background px-2 py-1 font-mono text-xs text-muted-foreground hover:border-primary/40 hover:text-foreground"
                >
                  Example {i + 1}
                </button>
              ))}
            </div>
          </form>
        </CardContent>
      </Card>

      <ResultPanel result={result} />
    </div>
  );
}

function ResultPanel({ result }: { result: ParseResult | null }) {
  return (
    <Card>
      <CardContent className="flex h-full flex-col p-5 md:p-6">
        <p className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Result
        </p>
        <div className="mt-4 flex-1 space-y-3">
          {!result && (
            <EmptyState>
              Paste a GS1 Digital Link URL or element string and press Decode. The detected
              Application Identifiers will appear here as cards.
            </EmptyState>
          )}

          {result?.ok === false && (
            <div className="flex flex-col gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-4">
              <div className="flex items-center gap-2 text-destructive">
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm font-medium">Couldn't parse</span>
              </div>
              <p className="text-sm text-muted-foreground">{result.reason}</p>
              {result.extras.length > 0 && (
                <ul className="text-xs text-muted-foreground">
                  {result.extras.map((e, i) => (
                    <li key={i} className="font-mono">
                      {e.key}
                      {e.value ? ` = ${e.value}` : ""}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {result?.ok && (
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center gap-2 text-emerald-600 dark:text-emerald-400">
                <CheckCircle2 className="h-4 w-4" />
                <span className="text-sm font-medium">
                  Parsed {result.elements.length}{" "}
                  {result.elements.length === 1 ? "element" : "elements"}
                </span>
                <Badge variant="outline" className="text-xs">
                  {shapeLabel(result.shape)}
                </Badge>
              </div>
              {result.elements.map((el, i) => (
                <ElementCard
                  key={i}
                  ai={el.ai}
                  label={el.label}
                  raw={el.raw}
                  formatted={el.formatted}
                  help={el.help}
                />
              ))}
              {result.extras.length > 0 && (
                <div className="rounded-md border border-border bg-background p-3 text-xs text-muted-foreground">
                  <div className="mb-1 font-medium text-foreground">Other fields</div>
                  <ul className="space-y-0.5">
                    {result.extras.map((e, i) => (
                      <li key={i} className="font-mono">
                        {e.key}
                        {e.value ? ` = ${e.value}` : ""}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function shapeLabel(s: ParseResult["shape"]): string {
  switch (s) {
    case "digital-link":
      return "Digital Link URL";
    case "element-string":
      return "Element string";
    case "raw-gs1":
      return "Raw GS1 payload";
    default:
      return s;
  }
}

function ElementCard({
  ai,
  label,
  raw,
  formatted,
  help,
}: {
  ai: string;
  label: string;
  raw: string;
  formatted: string;
  help: string;
}) {
  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Couldn't access clipboard");
    }
  };
  const showFormatted = formatted && formatted !== raw;
  return (
    <div className="rounded-md border border-border bg-background p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="secondary" className="font-mono text-xs">
              ({ai})
            </Badge>
            <span className="text-sm font-semibold">{label}</span>
          </div>
          <div className="mt-1.5 break-all font-mono text-sm">
            {raw || <span className="text-muted-foreground italic">empty</span>}
          </div>
          {showFormatted && (
            <div className="mt-0.5 text-xs text-muted-foreground">→ {formatted}</div>
          )}
          <p className="mt-1.5 text-xs text-muted-foreground">{help}</p>
        </div>
        {raw && (
          <Button size="sm" variant="ghost" onClick={() => copy(raw)} aria-label={`Copy ${label}`}>
            <Copy className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
}

function EmptyState({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-full min-h-32 flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-border bg-muted/30 p-6 text-center">
      <LinkIcon className="h-7 w-7 text-muted-foreground" aria-hidden />
      <p className="max-w-sm text-sm text-muted-foreground">{children}</p>
    </div>
  );
}

function SupportedAis() {
  const rows = [
    { ai: "(01)", label: "GTIN", notes: "14-digit Global Trade Item Number." },
    { ai: "(10)", label: "Batch / Lot", notes: "Variable, up to 20 characters." },
    { ai: "(17)", label: "Expiry", notes: "YYMMDD, formatted to YYYY-MM-DD." },
    { ai: "(21)", label: "Serial number", notes: "Variable, up to 20 characters." },
    { ai: "(11/13/15)", label: "Production / packaging / best-before dates", notes: "YYMMDD." },
    { ai: "(00)", label: "SSCC", notes: "18-digit Serial Shipping Container Code." },
    { ai: "(414)", label: "GLN", notes: "13-digit Global Location Number." },
    { ai: "(240/22/30/37)", label: "Variants & counts", notes: "Variant codes and count fields." },
    {
      ai: "(8003/8004/8006/8010/8017/8018)",
      label: "Asset & service identifiers",
      notes: "GRAI, GIAI, ITIP, CPID, GSRN.",
    },
  ];
  return (
    <>
      <h2>Supported Application Identifiers</h2>
      <p>
        The most commonly scanned GS1 AIs are recognised by AI number or by Digital Link short name
        (e.g. <code>/gtin/</code>, <code>/lot/</code>, <code>/expiry/</code>).
      </p>
      <TableScroll>
        <table>
          <thead>
            <tr>
              <th scope="col">AI</th>
              <th scope="col">Meaning</th>
              <th scope="col">Notes</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.ai}>
                <td>{r.ai}</td>
                <td>{r.label}</td>
                <td>{r.notes}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableScroll>
    </>
  );
}

function DigitalLinkExplainer() {
  return (
    <>
      <h2>What Is GS1 Digital Link?</h2>
      <p>
        GS1 Digital Link is a syntax that turns GS1 identifiers into a regular <code>https://</code> URL. The path encodes Application Identifiers as
        key/value pairs: a GTIN sits under <code>/01/</code>, a batch under <code>/10/</code>,
        and data such as an expiry date goes in the query string as <code>?17=</code>. That URL is then encoded into a 2D barcode —
        usually a QR code or a Data Matrix.
      </p>
      <p>
        The result is one barcode that does two jobs: a shopper's phone resolves the URL to a web
        page, while a retail or hospital scanner uses the same payload to extract the GTIN, batch
        and serial for inventory and traceability. No more separate marketing QR plus EAN-13.
      </p>
      <figure>
        <pre>
          https://id.gs1.org/01/07640156790000/10/ABC123?17=260131&21=42 │ │ │ │ │ │ │ │ │ └─ serial
          (21) │ │ │ │ │ │ │ └──────── expiry (17), 2026-01-31 │ │ │ │ │ └──────────────── batch / lot
          (10) │ │ │ └──────────────────────────────── GTIN value │ │
          └──────────────────────────────────── GTIN AI (01) │
          └────────────────────────────────────────────────── GS1 resolver host
          └─────────────────────────────────────────────────────────── any https:// host works
        </pre>
      </figure>
    </>
  );
}
function SunriseCallout() {
  return (
    <>
      <h2>Preparing for Sunrise 2027</h2>
      <p>
        Sunrise 2027 is GS1's global initiative for retail point-of-sale systems to accept 2D
        codes carrying GS1 Digital Link by the end of 2027. If you're auditing your supplier feeds
        or your own packaging artwork, this decoder is the fastest way to confirm a code parses
        cleanly. <a href="/barcode-guides/sunrise-2027-explained">Read the Sunrise 2027 guide</a>
        .
      </p>
    </>
  );
}

function DecodeHowTo() {
  return (
    <>
      <h2>How to Decode a GS1 Digital Link URL</h2>
      <p>
        Paste the full URL, including https://, into the box above and press Decode. Each Application Identifier becomes its own card; this parser checks structure and is not a GS1 Digital Link verifier.
      </p>
      <ol>
        <li>Copy the URL as text from your scanner app, without opening it.</li>
        <li>Paste it into the box and press Decode.</li>
        <li>Compare the GTIN, lot and expiry cards with the label, then check Other fields.</li>
      </ol>
      <h3>GS1 Digital Link Syntax</h3>
      <p>
        The decoder reads the path as key and value pairs from the first segment after the host, and reads query parameters the same way. Keys can be AI numbers or short names such as gtin, lot and ser. If a resolver adds its own folder, as in /p/01/…, delete it before decoding. The <Link to="/barcode-guides/gs1-digital-link">GS1 Digital Link guide</Link> explains the full format and syntax rules.
      </p>
    </>
  );
}

function DigitalLinkVsElementString() {
  return (
    <>
      <h2>GS1 Digital Link vs GS1-128 Element Strings</h2>
      <p>
        Both carry the same Application Identifiers and values: a Digital Link as a web address, an element string as AI and value pairs for GS1-128.
      </p>
      <TableScroll>
        <table>
          <thead>
            <tr>
              <th scope="col">Feature</th>
              <th scope="col">Digital Link URL</th>
              <th scope="col">Element string</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Field separator</td>
              <td>/ in the path, &amp; in the query</td>
              <td>FNC1, sent by scanners as GS (ASCII 29)</td>
            </tr>
            <tr>
              <td>Paste here as</td>
              <td>The full URL</td>
              <td>(AI)value pairs, or raw with ^ or &#123;GS&#125;</td>
            </tr>
          </tbody>
        </table>
      </TableScroll>
      <p>
        To encode AIs into a URL, use the <Link to="/gs1-digital-link-generator">GS1 Digital Link Generator</Link>, then paste its output here. Its net weight (AI 3103) appears under Other fields.
      </p>
    </>
  );
}

function RelatedTools() {
  const related = [
    {
      path: "/gs1-digital-link-generator",
      label: "GS1 Digital Link Generator",
      desc: "Build a Digital Link URL from a GTIN and AIs, then decode it here.",
    },
    {
      path: "/barcode-guides/gs1-digital-link",
      label: "GS1 Digital Link Guide",
      desc: "How the URL format, resolvers and path or query AIs fit together.",
    },
    {
      path: "/barcode-guides/sunrise-2027-explained",
      label: "Sunrise 2027 Guide",
      desc: "Plain-English explanation of the global move to 2D barcodes.",
    },
    {
      path: "/gtin-validator",
      label: "GTIN Validator",
      desc: "Validate the GTIN extracted from a Digital Link payload.",
    },
    {
      path: "/data-matrix-reader",
      label: "Data Matrix Reader",
      desc: "Scan a GS1 DataMatrix to get the payload you can paste here.",
    },
    {
      path: "/qr-code-reader",
      label: "QR Code Reader",
      desc: "Decode a GS1 Digital Link QR before parsing it.",
    },
    {
      path: "/check-digit-calculator",
      label: "Check Digit Calculator",
      desc: "Recompute a GTIN check digit when fixing a broken Digital Link.",
    },
    {
      path: "/barcode-reader",
      label: "Online Barcode Reader",
      desc: "Scan any 1D or 2D code, including GS1-formatted ones.",
    },
  ];
  return (
    <section className="mt-12">
      <h2 className="text-2xl font-semibold tracking-tight">Related Tools</h2>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {related.map((r) => (
          <a
            key={r.path}
            href={r.path}
            className="group rounded-lg border border-border bg-background p-4 transition-colors hover:border-primary/40"
          >
            <div className="text-sm font-semibold group-hover:text-primary">{r.label}</div>
            <div className="mt-1 text-sm text-muted-foreground">{r.desc}</div>
          </a>
        ))}
      </div>
    </section>
  );
}
